import { Stack } from '@mui/material';
import { Dropzone, type DropzoneProps, Show } from '@side/pantry';
import { useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { FileErrorAlert } from '../FileErrorAlert/FileErrorAlert';
import { BuyerAgreementFile } from './BuyerAgreementFile';

export type BuyerAgreementDropzoneWidthFileListProps = Omit<
  DropzoneProps,
  'inputAriaLabel' | 'dropAriaLabel' | 'acceptTypes' | 'fileTypesLabel'
> & {
  acceptedFiles?: File[];
  acceptTypes?: DropzoneProps['acceptTypes'];
  dropAriaLabel?: DropzoneProps['dropAriaLabel'];
  fileTypesLabel?: DropzoneProps['fileTypesLabel'];
  inputAriaLabel?: DropzoneProps['inputAriaLabel'];
};

export const BuyerAgreementDropzoneWidthFileList = ({
  acceptedFiles: acceptedFilesProp = [],
  acceptTypes = { 'application/pdf': ['.pdf'] },
  dropAriaLabel = 'Drag and drop or click to choose files. Supported file type: PDF smaller than 25MB. Maximum uploaded documents: 1.',
  fileTypesLabel = 'PDF',
  handleDrop,
  inputAriaLabel = 'upload file input',
  maxSizeLabel = '25MB',
  maxFiles = 1,
  ...props
}: BuyerAgreementDropzoneWidthFileListProps) => {
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>(acceptedFilesProp);
  const [rejections, setRejections] = useState<FileRejection[]>([]);
  const onDrop = (
    newAcceptedFiles: File[],
    newRejections: FileRejection[],
    method: 'drop' | 'click',
  ) => {
    setAcceptedFiles(newAcceptedFiles);
    setRejections(newRejections);
    handleDrop(newAcceptedFiles, newRejections, method);
  };

  const deleteFile = (file: File) => {
    setAcceptedFiles((prev) => prev.filter((f) => f !== file));
  };

  return (
    <>
      <Show when={rejections.length}>
        <FileErrorAlert
          remainingFiles={maxFiles - acceptedFiles.length}
          rejections={rejections}
          onClose={() => setRejections([])}
          supportedFileTypes={fileTypesLabel}
          maxFileSize={maxSizeLabel}
        />
      </Show>
      <Show
        when={acceptedFiles.length}
        fallback={
          <Dropzone
            handleDrop={onDrop}
            acceptTypes={acceptTypes}
            inputAriaLabel={inputAriaLabel}
            dropAriaLabel={dropAriaLabel}
            fileTypesLabel={fileTypesLabel}
            maxSizeLabel={maxSizeLabel}
            sx={{
              margin: 0,
            }}
            {...props}
          />
        }
      >
        <Stack direction="column" spacing={1}>
          {acceptedFiles.map((file) => (
            <BuyerAgreementFile
              key={file.name}
              file={file}
              deleteFile={deleteFile}
            />
          ))}
        </Stack>
      </Show>
    </>
  );
};
